import React, { useState, useEffect, useRef } from "react"
import CookiesIcon from "icons/cookies2.svg"
import AngleDown from "icons/angle_down.svg"
import AngleUp from "icons/angle_up.svg"
import FormInputLightswitch from "./forms/form-components/form-input-lightswitch"
import { useForm } from "react-hook-form"
import {
  saveConsent,
  getConsent,
  DEFAULT_SETTINGS,
  ALL_ACCEPTED,
  NONE_ACCEPTED,
  CookieConsent,
} from "../utils/cookie-consent"

type ConsentListenerCallback = (CookieConsent: CookieConsent) => void

const CookieConsentModal = () => {
  const [showModal, setShowModal] = useState(false)
  const [settingsActive, setSettingsActive] = useState(false)
  const [consentListeners, setConsentListeners] = useState<
    ConsentListenerCallback[]
  >([])
  const settingsWrapperRef = useRef<HTMLFormElement>(null)

  const { handleSubmit, control } = useForm({
    defaultValues: DEFAULT_SETTINGS,
  })

  const acceptAll = () => {
    onConsentChange(ALL_ACCEPTED)
  }

  const declineAll = () => {
    onConsentChange(NONE_ACCEPTED)
  }

  const onSubmit = handleSubmit((consent: CookieConsent) => {
    onConsentChange(consent)
  })

  /**
   * Called when user grants/denies consent.
   * @param {CookieConsent} consent represent the consent level
   */
  const onConsentChange = (consent: CookieConsent) => {
    setShowModal(false)
    saveConsent(consent)
    consentListeners.forEach(callback => {
      callback(consent)
    })

    window.dataLayer = window.dataLayer || []
    window.dataLayer.push({
      event: "cookie_consent_update",
      path: window.location.pathname,
    })
  }

  useEffect(() => {
    /**
     * Called from GTM template Consent Mode to set callback to be executed when user consent is provided.
     * @param {function} callback to execute on user consent
     */
    window.addConsentListener = (callback: () => void) => {
      setConsentListeners(consentListeners => [...consentListeners, callback])
    }
  }, [])

  useEffect(() => {
    if (settingsWrapperRef.current) {
      settingsWrapperRef.current.style.maxHeight = settingsActive
        ? `${settingsWrapperRef.current.scrollHeight + 5}px`
        : "0px"
    }
  }, [settingsActive])

  useEffect(() => {
    const hasNoPreviousConsent = getConsent() === undefined
    const isOnCookieNoticePage =
      typeof window !== "undefined" &&
      window.location.pathname === "/cookie-notice/"
    const isPreviewSite = process.env.GATSBY_IS_PREVIEW
    setShowModal(
      hasNoPreviousConsent && !isOnCookieNoticePage && !isPreviewSite,
    )
  }, [])

  if (!showModal) {
    return null
  }

  return (
    <div className="c-cookie-consent">
      <div className="c-cookie-consent__inner">
        <CookiesIcon className="c-cookie-consent__logo" />
        <p className="c-cookie-consent__paragraph">
          We use cookies to enhance your experience on this website and for
          advertisements. Read more{" "}
          <a href="/cookie-notice/" target="_blank" className="c-link -plain">
            here
          </a>
          .
        </p>
        <div>
          <button
            className="c-cta -green -accept"
            type="button"
            onClick={acceptAll}
          >
            Allow all
          </button>
          <button
            className="c-cta -outline-dark -decline"
            type="button"
            onClick={declineAll}
          >
            Reject all
          </button>
        </div>
        <div>
          <button
            className="c-link c-cookie-consent__expand-settings-button"
            onClick={() => setSettingsActive(!settingsActive)}
            aria-expanded={settingsActive}
            aria-controls={"c-cookie-consent__settings-wrapper"}
          >
            Cookie settings
            <span className="u-visually-hidden">
              {settingsActive ? "Collapse" : "Expand"}
            </span>
            {settingsActive ? (
              <AngleUp className="c-cookie-consent__angle" />
            ) : (
              <AngleDown className="c-cookie-consent__angle" />
            )}
          </button>
        </div>
        <form
          onSubmit={onSubmit}
          className="c-cookie-consent__settings-wrapper"
          ref={settingsWrapperRef}
        >
          <div className="c-cookie-consent__settings-item">
            <span>
              Necessary <span>(Always active)</span>
            </span>
            <FormInputLightswitch
              name="necessary"
              control={control}
              checked={true}
              disabled={true}
            />
          </div>
          <div className="c-cookie-consent__settings-item">
            <span>Analytical</span>
            <FormInputLightswitch name="analytics_storage" control={control} />
          </div>
          <div className="c-cookie-consent__settings-item">
            <span>Marketing</span>
            <FormInputLightswitch name="ad_storage" control={control} />
          </div>
          <button
            type="submit"
            className="c-link c-cookie-consent__save-settings-button"
          >
            Save settings
          </button>
        </form>
      </div>
    </div>
  )
}

export default CookieConsentModal
